// src/lib/supabase.js - Supabase client configuration for Create React App
import { createClient } from '@supabase/supabase-js'
import logger from '../services/logger'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  logger.error('Missing Supabase environment variables')
  logger.error('Required: REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY')
  logger.error('Please add these to your .env.local file')

  // Don't throw error immediately - allow app to start for development
  // The auth components will handle the missing config gracefully
}

// Create Supabase client
export const supabase = supabaseUrl && supabaseAnonKey ? createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false, // Disabled - we're not using Supabase OAuth, prevents conflicts with Spotify/YouTube OAuth params
    // Use localStorage for session persistence (compatible with existing strategy)
    storage: window.localStorage,
    flowType: 'pkce' // More secure auth flow (Proof Key for Code Exchange)
  }
}) : null

// Helper function to check if Supabase is configured
export const isSupabaseConfigured = () => {
  return !!(supabaseUrl && supabaseAnonKey && supabase)
}

// Helper function to get current session
export const getCurrentSession = async () => {
  if (!supabase) return null

  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) throw error
    return session
  } catch (error) {
    logger.error('Error getting session:', error)
    return null
  }
}

// Helper function to get current user
export const getCurrentUser = async () => {
  if (!supabase) return null

  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return user
  } catch (error) {
    logger.error('Error getting user:', error)
    return null
  }
}

if (supabase) {
  logger.success('Supabase client initialized:', supabaseUrl)
} else {
  logger.warn('Supabase client not initialized - environment variables missing')
}
